import React, { ReactElement, useContext } from 'react';
import styled from 'styled-components';
import AccountPicker from '../items/AccountPicker';
import { HOME_ROUTE } from 'src/constants/UrlConstants';
import {
  APP_CONTAINER_BASE_URL,
  UI_CLIENTS,
} from 'src/constants/GlobalConstants';
import { AuthContext } from 'src/state/providers/AuthProvider';
import { getPathname } from 'src/utilities/HelperUtil';

const MenuContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

export interface IDynamicMenu {
  dynamicMenuItems?: IMenuItem[];
}
export interface IMenuItem {
  key: number; //should be unique
  order: number;
  component: ReactElement;
  allowedRoutes?: string[];
  disallowedRoutes?: string[];
  allowedClients?: UI_CLIENTS[];
}

export const DynamicMenuItems: IMenuItem[] = [
  {
    key: 1,
    order: 1,
    component: <AccountPicker />,
    allowedClients: [UI_CLIENTS.VICI],
    // Account Picker should not be rendered for SPIM users
    disallowedRoutes: [APP_CONTAINER_BASE_URL.SPIM],
  },
];

/**
 * Get Hash part of the URL excluding #
 * @returns hash without #
 */
export function getHash() {
  const hash = window.location.hash;
  if (!hash || hash.length === 0) {
    return HOME_ROUTE;
  }
  return hash.substring(1, hash.length);
}

/**
 * Filter the Menu Items based on the allowed routes.
 * @param dynamicMenuItems IMenuItem[]
 * @returns filtered IMenuItem[]
 */
export function filterMenuItems(
  dynamicMenuItems: IMenuItem[],
  client: UI_CLIENTS,
): IMenuItem[] {
  const hash = getHash();
  const pathname = getPathname();
  return dynamicMenuItems.filter(
    (dynamicMenuItem) =>
      allowedInRoutes(hash, dynamicMenuItem.allowedRoutes) &&
      allowedClients(client, dynamicMenuItem.allowedClients) &&
      !isDisallowedRoute(pathname, dynamicMenuItem.disallowedRoutes),
  );
}

/**
 * Checks if a given pathname matches any of the disallowed routes.
 */
export function isDisallowedRoute(
  pathname: string,
  disallowedRoutes?: string[],
): boolean {
  if (!disallowedRoutes || disallowedRoutes.length === 0) {
    return false;
  }

  return disallowedRoutes.some((route) => pathname?.includes(route) ?? false);
}

export function allowedInRoutes(hash: string, allowedRoutes?: string[]) {
  if (!allowedRoutes) {
    return true;
  }
  return allowedRoutes.length > 0 && allowedRoutes.includes(hash)
    ? true
    : false;
}

export function allowedClients(client: UI_CLIENTS, allowedClients?: string[]) {
  if (!allowedClients) {
    return true;
  }
  return allowedClients.length > 0 && allowedClients.includes(client)
    ? true
    : false;
}

/**
 * @TODO
 * 1. Update the state of DynamicMenu component after URL changes for incorporation of filtered routes.
 * 2. Sort the list based on the order.
 */
const DynamicMenu: React.FC<IDynamicMenu> = ({
  dynamicMenuItems = DynamicMenuItems,
}) => {
  const userAttributes = useContext(AuthContext);
  const filteredMenuItems = filterMenuItems(
    dynamicMenuItems,
    userAttributes.clientId,
  );
  if (filteredMenuItems.length === 0) {
    return null;
  }
  return (
    <MenuContainer data-testid="dynamicMenuItems">
      {filteredMenuItems.map((filterMenuItem) => (
        <div key={filterMenuItem.key}>{filterMenuItem.component}</div>
      ))}
    </MenuContainer>
  );
};

export default DynamicMenu;
